import {
  type Vertical,
  type InstrumentedComponent,
} from '@web-platform/shared-universal-types-tracking/src/MBDTypes';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { v4 as uuid } from 'uuid';

import { buildImpressionEvent, buildInteractionEvent } from './utils/buildEvent';
import { sendEvent, sendEventWithDebounce } from './utils/sendEvent';

import type { IMBDEventTracking } from '@web-platform/shared-universal-types-tracking/src/IMBDEventTracking';

export const MBD_EVENT_NAME = 'mbd-web';
export const MBD_IMPRESSION_SCHEMA = 'user_behaviour_data_domain.ElementImpression';
export const MBD_INTERACTION_SCHEMA = 'user_behaviour_data_domain.ElementInteraction';
export const ERROR_LOG_COMPONENT_NAME = 'MBDEventTracking';

export class MBDEventTracking implements IMBDEventTracking {
  viewGuid: string;

  elementImpressionId?: string;

  componentName: InstrumentedComponent;

  vertical: Vertical;

  stringProperties?: Record<string, string>;

  allowTracking: boolean = false;

  constructor({
    componentName,
    stringProperties,
    vertical,
    viewGuid,
  }: {
    componentName: InstrumentedComponent;
    stringProperties?: Record<string, string>;
    vertical: Vertical;
    viewGuid: string;
  }) {
    this.vertical = vertical;
    this.componentName = componentName;
    this.viewGuid = viewGuid;
    this.stringProperties = stringProperties;

    if (!viewGuid) {
      const error = new Error(
        `Failed to track meaningful behavioural events of ${componentName} in ${vertical}, missing view guid.`,
      );
      logError({
        error,
        component: ERROR_LOG_COMPONENT_NAME,
        message: 'Missing view guid',
      });
      return;
    }

    this.allowTracking = true;
  }

  trackImpression(
    numericProperties?: Record<string, number>,
    booleanProperties?: Record<string, boolean>,
  ) {
    if (!this.allowTracking) return;
    this.elementImpressionId = uuid();

    const event = buildImpressionEvent(this.viewGuid, {
      componentName: this.componentName,
      vertical: this.vertical,
      stringProperties: this.stringProperties,
      numericProperties,
      booleanProperties,
    });

    sendEventWithDebounce(event, this.elementImpressionId);
  }

  trackInteraction(
    numericProperties?: Record<string, number>,
    booleanProperties?: Record<string, boolean>,
  ) {
    if (!this.allowTracking) return Promise.resolve();
    if (!this.elementImpressionId) {
      const error = new Error(
        `Failed to track interaction event of ${this.componentName} in ${this.vertical}, missing elementImpressionId.`,
      );
      logError({
        error,
        component: ERROR_LOG_COMPONENT_NAME,
        message:
          'Missing elementImpressionId, you may need to call trackImpression first or use trackInteractionOnly instead.',
      });
      return Promise.resolve();
    }

    const event = buildInteractionEvent(this.elementImpressionId, {
      componentName: this.componentName,
      vertical: this.vertical,
      stringProperties: this.stringProperties,
      numericProperties,
      booleanProperties,
    });

    return sendEvent(event, this.elementImpressionId);
  }
}
